import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Check } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ScrollArea } from "@/components/ui/scroll-area";

type Plan = {
  name: string;
  price: string;
  features: string[];
  productLimit: number | "Illimité";
  type: "free" | "standard" | "premium";
};

const plans: Plan[] = [
  {
    name: "Gratuit",
    price: "0€",
    features: ["3 produits maximum", "Fonctionnalités de base", "Support par email"],
    productLimit: 3,
    type: "free"
  },
  {
    name: "Standard",
    price: "10€/mois",
    features: [
      "20 produits maximum",
      "Toutes les fonctionnalités de base",
      "Support prioritaire",
      "Statistiques avancées"
    ],
    productLimit: 20,
    type: "standard"
  },
  {
    name: "Premium",
    price: "25€/mois",
    features: [
      "Produits illimités",
      "Toutes les fonctionnalités",
      "Support premium 24/7",
      "Statistiques avancées"
    ],
    productLimit: "Illimité",
    type: "premium"
  }
];

export const ManageSubscriptionDialog = ({ currentPlan }: { currentPlan: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleUpgrade = async (planType: string) => {
    try {
      setIsLoading(true);

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        toast.error("Vous devez être connecté pour effectuer cette action");
        setIsLoading(false);
        return;
      }

      const userId = user.id;
      
      if (planType === 'free') {
        const { data: subscriptionData, error: subError } = await supabase
          .from('subscriptions')
          .select('stripe_subscription_id')
          .eq('owner_id', userId)
          .single();

        if (subError) {
          console.error("Erreur lors de la récupération de l'abonnement:", subError);
          throw subError;
        }

        if (subscriptionData?.stripe_subscription_id) {
          const { error: cancelError } = await supabase.functions.invoke('cancel-subscription', {
            body: { 
              subscriptionId: subscriptionData.stripe_subscription_id,
              userId
            }
          });

          if (cancelError) {
            console.error("Erreur lors de l'annulation de l'abonnement:", cancelError);
            throw cancelError;
          }
        }

        const { error: updateError } = await supabase
          .from('subscriptions')
          .update({ 
            plan: 'free',
            product_limit: 3,
            stripe_subscription_id: null,
            stripe_customer_id: null
          })
          .eq('owner_id', userId);

        if (updateError) {
          console.error("Erreur lors de la mise à jour de l'abonnement:", updateError);
          throw updateError;
        }

        toast.success("Vous êtes passé au plan gratuit avec succès");
        setIsOpen(false);
        return;
      }

      const productLimit = planType === 'premium' ? -1 : 20;
      console.log("Tentative de mise à niveau vers:", planType, "pour l'utilisateur:", userId);

      const { data: checkoutData, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { 
          planType,
          userId,
          productLimit
        }
      });

      if (error) {
        console.error("Erreur lors de la création de la session de paiement:", error);
        throw error;
      }

      if (!checkoutData?.url) {
        throw new Error("Aucune URL de paiement n'a été reçue");
      }

      console.log("URL de redirection Stripe reçue:", checkoutData.url);
      
      setIsOpen(false);
      
      window.location.href = checkoutData.url;

    } catch (error: any) {
      console.error("Erreur lors de la mise à niveau:", error);
      toast.error(error.message || "Une erreur est survenue lors de la mise à niveau de l'abonnement");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full">
          Gérer l'abonnement
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[90vw] lg:max-w-4xl h-[90vh] sm:h-auto p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle>Choisir un plan</DialogTitle>
        </DialogHeader>
        <ScrollArea className="p-6 h-full">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map((plan) => (
              <Card key={plan.name} className={`flex flex-col ${plan.type === currentPlan ? 'border-primary' : ''}`}>
                <CardHeader>
                  <CardTitle className="flex justify-between items-center">
                    {plan.name}
                    {plan.type === currentPlan && (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                        Plan actuel
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow">
                  <div className="text-2xl font-bold mb-4">{plan.price}</div>
                  <ul className="space-y-2">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button
                    className="w-full"
                    variant={plan.type === currentPlan ? "outline" : "default"}
                    disabled={plan.type === currentPlan || isLoading}
                    onClick={() => handleUpgrade(plan.type)}
                  >
                    {isLoading
                      ? "Chargement..."
                      : plan.type === currentPlan
                      ? "Plan actuel"
                      : plan.type === "free"
                      ? "Rétrograder"
                      : "Mettre à niveau"}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
